import React from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';

const FeedItem = ({ event, onRestaurantPress }) => {
  // Try client-side resolution first, fall back to server-side
  const clientSideProfilePicture = useQuery(
    api.images.getStorageUrl,
    event.user?.pfp ? { storageId: event.user.pfp } : "skip"
  );
  
  // Use client-side result if available, otherwise fall back to server-side
  const profilePicture = clientSideProfilePicture || event.userPfpUrl;

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');
    
    // Format based on length
    if (digits.length === 10) {
      // US format: (XXX) XXX-XXXX
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      // US format with country code: 1 (XXX) XXX-XXXX
      return `1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    } else {
      // For other formats, just show the digits with some spacing
      return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3');
    }
  };

  const getDisplayName = (user) => {
    if (user?.username) {
      return `@${user.username}`;
    } else if (user?.name) {
      return user.name;
    } else if (user?.phoneNumber) {
      return formatPhoneNumber(user.phoneNumber);
    } else {
      return 'unknown';
    }
  };

  const getCheckinMessage = (event) => {
    if (event.firstCheckin) {
      return `checked in for the first time at`;
    }
    return `checked in at`;
  };

  const getVisitCount = (event) => {
    if (event.checkinCount === 1) {
      return "1 time";
    } else if (event.checkinCount > 1) {
      return `${event.checkinCount} times`;
    } else {
      return "1 time"; // Fallback for first-time check-ins
    }
  };

  const getTierUpgradeMessage = (event) => {
    return `upgraded to ${event.newTier} tier at`;
  };

  const getPassEventMessage = (event) => {
    switch (event.actionType) {
      case 'claimed':
        return `claimed ${event.pass?.title || 'a pass'} at`;
      case 'gifted':
        return `gifted ${event.pass?.title || 'a pass'} to @${event.giftedToUser?.username || event.giftedToUser?.name || 'someone'} at`;
      default:
        return `interacted with ${event.pass?.title || 'a pass'} at`;
    }
  };

  const getPassEventIcon = (actionType) => {
    switch (actionType) {
      case 'claimed':
        return 'checkmark-circle';
      case 'gifted':
        return 'gift';
      default:
        return 'card';
    }
  };

  const getPassEventColor = (actionType) => {
    switch (actionType) {
      case 'claimed':
        return '#4CAF50';
      case 'gifted':
        return '#FF9800';
      default:
        return '#9C27B0';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'Discovery':
        return 'star-outline';
      case 'Silver':
        return 'star';
      case 'Gold':
        return 'star';
      case 'Platinum':
        return 'star';
      case 'Diamond':
        return 'star';
      default:
        return 'star-outline';
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Discovery':
        return '#C0C0C0';
      case 'Silver':
        return '#C0C0C0';
      case 'Gold':
        return '#FFD700';
      case 'Platinum':
        return '#E5E4E2';
      case 'Diamond':
        return '#B9F2FF';
      default:
        return '#C0C0C0';
    }
  };

  // Determine event type and render appropriate content
  const renderEventContent = () => {
    // Check if this is a pass event
    if (event.actionType && ['claimed', 'gifted'].includes(event.actionType)) {
      return (
        <View style={styles.passEventContent}>
          <Text style={styles.checkinMessage}>
            {getPassEventMessage(event)} <Text style={styles.restaurantName}>{event.restaurant?.name || 'Unknown Restaurant'}</Text>
          </Text>
          <View style={styles.passInfo}>
            <Ionicons 
              name={getPassEventIcon(event.actionType)} 
              size={16} 
              color={getPassEventColor(event.actionType)} 
            />
            <Text style={styles.passText}>
              {event.pass?.title || 'Pass'}
            </Text>
          </View>
        </View>
      );
    }

    // Check if this is a tier upgrade event
    if (event.oldTier && event.newTier) {
      return (
        <View style={styles.tierUpgradeContent}>
          <Text style={styles.checkinMessage}>
            {getTierUpgradeMessage(event)} <Text style={styles.restaurantName}>{event.restaurant?.name || 'Unknown Restaurant'}</Text>
          </Text>
          <View style={styles.tierInfo}>
            <Ionicons 
              name={getTierIcon(event.newTier)} 
              size={16} 
              color={getTierColor(event.newTier)} 
            />
            <Text style={styles.tierText}>
              {event.oldTier} → {event.newTier}
            </Text>
          </View>
        </View>
      );
    }

    // Default to check-in event
    return (
      <>
        <Text style={styles.checkinMessage}>
          {getCheckinMessage(event)} <Text style={styles.restaurantName}>{event.restaurant?.name || 'Unknown Restaurant'}</Text>
        </Text>
        <Text style={styles.visitInfo}>
          they've gone {getVisitCount(event)} - check it out?
        </Text>
      </>
    );
  };

  return (
    <TouchableOpacity 
      style={styles.container}
      onPress={() => onRestaurantPress?.(event.restaurant)}
    >
      {/* Profile Picture Circle */}
      <View style={styles.avatarContainer}>
        {profilePicture ? (
          <Image 
            source={{ uri: profilePicture }} 
            style={styles.avatar}
          />
        ) : (
          <View style={styles.avatarPlaceholder}>
            <Ionicons name="person" size={20} color="#666" />
          </View>
        )}
        
        {/* Restaurant Picture Circle - Now below profile */}
        <View style={styles.restaurantAvatarContainer}>
          {event.restaurant?.isPartner && event.restaurant?.imgUrl ? (
            <Image source={{ uri: event.restaurant.imgUrl }} style={styles.restaurantAvatar} />
          ) : (
            <Image 
              source={require('../assets/nonDisgoPartnerMembershipCard.jpg')} 
              style={styles.restaurantAvatar} 
            />
          )}
        </View>
      </View>
      
      {/* Content Area */}
      <View style={styles.content}>
        <Text style={styles.username}>
          {getDisplayName(event.user)}
        </Text>
        
        {renderEventContent()}
      </View>

    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#F0F0F0',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 4,
  },
  avatarContainer: {
    marginRight: 16,
    alignItems: 'center',
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: '#F0F0F0',
    marginBottom: 8,
  },
  avatarPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#F0F0F0',
    marginBottom: 8,
  },
  content: {
    flex: 1,
    marginRight: 16,
  },
  username: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1A1A1A',
    marginBottom: 4,
  },
  checkinMessage: {
    fontSize: 14,
    color: '#4A4A4A',
    marginBottom: 4,
    lineHeight: 20,
  },
  restaurantName: {
    fontWeight: '600',
    color: '#2D2D2D',
  },
  visitInfo: {
    fontSize: 13,
    color: '#6B6B6B',
    lineHeight: 18,
  },
  tierUpgradeContent: {
    marginBottom: 4,
  },
  tierInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
  },
  tierText: {
    fontSize: 13,
    color: '#4A4A4A',
    marginLeft: 6,
    fontWeight: '500',
  },
  passEventContent: {
    marginBottom: 4,
  },
  passInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
  },
  passText: {
    fontSize: 13,
    color: '#4A4A4A',
    marginLeft: 6,
    fontWeight: '500',
  },
  restaurantAvatarContainer: {
    alignItems: 'center',
    marginTop: -14,
  },
  restaurantAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: '#F0F0F0',
  },
  restaurantAvatarPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#F0F0F0',
  },
});

export default FeedItem; 